// Light / Dark / System, like the CRM's own appearance setting. The choice is remembered in this browser.
// Dark is the CRM's `.dark` class on <html>, so every --t-* token flips with it.
import { useSyncExternalStore } from 'react';

export type ThemeChoice = 'light' | 'dark' | 'system';
export type Resolved = 'light' | 'dark';

const KEY = 'ewings-desk-theme';
const listeners = new Set<() => void>();
const media = typeof window !== 'undefined' ? window.matchMedia('(prefers-color-scheme: dark)') : null;

export const readChoice = (): ThemeChoice => {
  try {
    const stored = localStorage.getItem(KEY);

    return stored === 'light' || stored === 'dark' ? stored : 'system';
  } catch {
    return 'system';
  }
};

export const resolve = (choice: ThemeChoice): Resolved => (choice === 'system' ? (media?.matches === true ? 'dark' : 'light') : choice);

export const apply = (choice: ThemeChoice): Resolved => {
  const resolved = resolve(choice);
  const root = document.documentElement;

  root.classList.toggle('dark', resolved === 'dark');
  root.classList.toggle('light', resolved === 'light');
  root.setAttribute('data-theme', resolved);

  return resolved;
};

let snapshot: Resolved = typeof document === 'undefined' ? 'light' : apply(readChoice());

const notify = (): void => {
  snapshot = apply(readChoice());
  listeners.forEach((l) => l());
};

export const setChoice = (choice: ThemeChoice): void => {
  try {
    if (choice === 'system') localStorage.removeItem(KEY);
    else localStorage.setItem(KEY, choice);
  } catch {
    // private mode: the choice still applies for this visit
  }

  notify();
};

// "System" follows the OS live, including when it flips at sunset.
media?.addEventListener('change', () => {
  if (readChoice() === 'system') notify();
});

export const useResolvedScheme = (): Resolved =>
  useSyncExternalStore(
    (cb) => {
      listeners.add(cb);

      return () => listeners.delete(cb);
    },
    () => snapshot,
    () => 'light',
  );

export const useChoice = (): ThemeChoice =>
  useSyncExternalStore(
    (cb) => {
      listeners.add(cb);

      return () => listeners.delete(cb);
    },
    () => readChoice(),
    () => 'system',
  );
